import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/components/ConfirmDialog";
import { HealthScoreBadge } from "@/components/HealthScoreBadge";

const STATUS_COLORS = {
  Draft: "bg-slate-800 text-slate-300 border-slate-700",
  "Ready for Review": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  "Under Review": "bg-amber-500/10 text-amber-400 border-amber-500/20",
  "Revision Required": "bg-orange-500/10 text-orange-400 border-orange-500/20",
  Approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Rejected: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  Issued: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

const STATUSES = ["All", "Draft", "Ready for Review", "Under Review", "Revision Required", "Approved", "Rejected", "Issued"];

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const { isDirector, canCreateProject } = useAuth();
  const confirm = useConfirm();

  const fetchProjects = () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "All") params.status = statusFilter;
    API.get("/projects", { params })
      .then((r) => { setProjects(r.data); setSelected(new Set()); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, [search, statusFilter]);

  const handleDelete = async (id, name) => {
    if (!(await confirm({ title: "Delete Project", description: `Delete project "${name}"? This cannot be undone.`, confirmText: "Delete Project", variant: "destructive" }))) return;
    try {
      await API.delete(`/projects/${id}`);
      toast.success("Project deleted");
      fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete project"); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === projects.length) setSelected(new Set());
    else setSelected(new Set(projects.map(p => p.id)));
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    if (!(await confirm({ title: "Delete Selected Projects", description: `Delete ${selected.size} selected project(s)? This cannot be undone.`, confirmText: `Delete ${selected.size} Project(s)`, variant: "destructive" }))) return;
    setDeleting(true);
    try {
      const r = await API.post("/projects/batch-delete", { project_ids: [...selected] });
      toast.success(r.data.message);
      fetchProjects();
    } catch (e) { toast.error(e.response?.data?.detail || "Batch delete failed"); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="project-list" className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">{projects.length} project{projects.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {isDirector && selected.size > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBatchDelete} disabled={deleting} data-testid="batch-delete-btn" className="h-9">
              <Trash2 className="w-4 h-4 mr-1.5" />{deleting ? "Deleting..." : `Delete ${selected.size}`}
            </Button>
          )}
          {canCreateProject && (
            <Link to="/projects/new">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-lg shadow-blue-900/20" data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="project-search"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="status-filter-trigger">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase().replace(/\s/g, "-")}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-visible">
        <CardContent className="p-0">
          <div className="table-scroll-wrapper">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {isDirector && (
                  <TableHead className="w-10 px-3">
                    <Checkbox checked={projects.length > 0 && selected.size === projects.length} onCheckedChange={toggleSelectAll} data-testid="select-all-projects" />
                  </TableHead>
                )}
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Owner</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Sector</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Disciplines</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Status</TableHead>
                {isDirector && <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Health</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4">Updated</TableHead>
                {isDirector && <TableHead className="text-xs uppercase tracking-wider font-medium text-muted-foreground px-4 w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`} className="border-border">
                    <TableCell colSpan={isDirector ? 10 : 8} className="px-4 py-3"><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>
                  </TableRow>
                ))
              ) : projects.length > 0 ? (
                projects.map((p) => (
                  <TableRow key={p.id} className={`border-border hover:bg-accent/30 ${selected.has(p.id) ? "bg-primary/5" : ""}`} data-testid={`project-row-${p.id}`}>
                    {isDirector && (
                      <TableCell className="px-3">
                        <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-project-${p.id}`} />
                      </TableCell>
                    )}
                    <TableCell className="px-4 py-3">
                      <Link to={`/projects/${p.id}`} className="text-sm font-medium text-foreground hover:text-primary transition-colors" data-testid={`project-link-${p.id}`}>{p.name}</Link>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-muted-foreground">{p.client_name}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-muted-foreground">{p.owner_name || "\u2014"}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-muted-foreground">{p.sector || "\u2014"}</TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(p.disciplines || []).map((d) => (
                          <span key={d} className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded">{d.charAt(0)}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[p.status] || STATUS_COLORS.Draft}`}>{p.status}</span>
                    </TableCell>
                    {isDirector && (
                      <TableCell className="px-4 py-3">
                        {p.health_score && p.health_score.color !== "gray" ? (
                          <HealthScoreBadge healthScore={p.health_score} size="compact" />
                        ) : (
                          <span className="text-xs text-muted-foreground">{"\u2014"}</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="px-4 py-3 font-mono text-xs text-muted-foreground">{new Date(p.updated_at).toLocaleDateString("en-GB")}</TableCell>
                    {isDirector && (
                      <TableCell className="px-4 py-3">
                        <button onClick={() => handleDelete(p.id, p.name)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-project-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow className="border-border">
                  <TableCell colSpan={isDirector ? 10 : 8} className="text-center py-12 text-sm text-muted-foreground">
                    No projects found. <Link to="/projects/new" className="text-primary font-medium">Create one</Link>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
